import type { SuggesterSection, SuggesterStation, SuggesterStatus } from './types'

/**
 * Inputs the section builder needs — the relevant slice of
 * {@link SuggesterState} plus the host-supplied station catalogue and
 * the backend Popular list.
 */
export interface BuildSectionsArgs {
  readonly status: SuggesterStatus
  readonly results: readonly SuggesterStation[]
  /** Nearby stations — full list in `nearby`, 3 closest in `idle`. */
  readonly nearby: readonly SuggesterStation[]
  readonly recentIds: readonly string[]
  /**
   * Кластерные `naptanId` избранного — стор keyed by cluster, не по схемным
   * `station.id`. Одна физическая станция — N записей каталога (King's Cross
   * — семь); ключ по записи давал семь одинаковых строк.
   */
  readonly favoriteIds: readonly string[]
  /** Full station catalogue — resolves recent / favourite ids to stations. */
  readonly catalogue: readonly SuggesterStation[]
  /** Backend-provided Popular fallback (8 stations). */
  readonly popular: readonly SuggesterStation[]
}

/**
 * Total visible idle budget — cross-platform STRICT (spec §3 / iOS
 * `SuggesterSystem.idleVisibleBudget`). Nearby + Favorites + Recents +
 * Popular cap at 8 unique rows; Popular fills the remainder.
 */
export const IDLE_VISIBLE_BUDGET = 8

/**
 * Compose the {@link SuggesterSection}s the UI renders, per spec §3/§4.
 *
 * - `idle`: **always up to 4 sections** in fixed DISPLAY order — Favorites,
 *   Nearby (3 closest), Recents, Popular — each skipped only when its
 *   (post-dedup) source is empty. **TASK-412 AA — each station appears in
 *   exactly ONE idle section.** Dedup PRECEDENCE is FAVORITES > NEARBY >
 *   RECENTS > POPULAR (independent of display order): Favorites is PROTECTED
 *   (kept verbatim), and each lower-priority section subtracts the ids
 *   already claimed by every higher-priority section — so a favorited +
 *   recent station shows only under Favorites. The total caps at
 *   {@link IDLE_VISIBLE_BUDGET} (8) unique rows: Popular fills the remainder
 *   after the top three and is dropped when they already produce ≥ 8 rows.
 *   Mirrors the iOS stage-2 dedup-precedence + `popularBudget` model —
 *   Popular is NOT a "fallback when all empty"; it always backfills.
 * - `results` / `offline`: a single `results` section in match order.
 * - `nearby`: a single `nearby` section — the full distance-sorted list.
 * - `loading` / `empty`: no sections (spinner / message rendered instead).
 *
 * Recent ids resolve by `station.id`; favourite ids are cluster `naptanId`s
 * resolving to the cluster representative (lowest `id`). Ids that no longer
 * resolve against `catalogue` (schema drift) are silently dropped.
 */
export function buildSections(args: BuildSectionsArgs): readonly SuggesterSection[] {
  const { status, results, nearby, recentIds, favoriteIds, catalogue, popular } = args

  if (status === 'results' || status === 'offline') {
    return [{ key: 'results', stations: results }]
  }
  if (status === 'nearby') {
    return [{ key: 'nearby', stations: nearby }]
  }
  if (status !== 'idle') return []

  const byId = new Map(catalogue.map((s) => [s.id, s]))
  const resolve = (ids: readonly string[]): readonly SuggesterStation[] =>
    ids.map((id) => byId.get(id)).filter((s): s is SuggesterStation => s !== undefined)

  // naptanId → запись-представитель кластера (минимальный `id` — тот же
  // детерминизм, что iOS `StationClusterIndex` и Android
  // `clusterRepresentativeByNaptan`): иначе строка избранного меняла бы
  // линию-подпись между рендерами при неизменных данных.
  const byNaptan = new Map<string, SuggesterStation>()
  for (const s of catalogue) {
    if (s.naptanId === undefined || s.naptanId === '') continue
    const existing = byNaptan.get(s.naptanId)
    if (existing === undefined || s.id < existing.id) byNaptan.set(s.naptanId, s)
  }
  const favorites = favoriteIds
    .map((id) => byNaptan.get(id))
    .filter((s): s is SuggesterStation => s !== undefined)
  const recents = resolve(recentIds)

  // TASK-412 AA — idle-section dedup so each station appears in exactly ONE
  // idle section. The dedup PRECEDENCE is FAVORITES > NEARBY > RECENTS >
  // POPULAR: Favorites is PROTECTED (kept verbatim, never deduped out); each
  // lower-priority section subtracts the ids already claimed by every
  // higher-priority section. (Mirrors iOS @2760285a0 — e.g. a favorited +
  // recent station like Mornington Crescent renders only under Favorites.)
  // The DISPLAY order is independent and stays fixed Nearby → Favorites →
  // Recents → Popular; only membership is decided by precedence.
  const claimed = new Set<string>()
  const claim = (stations: readonly SuggesterStation[]): readonly SuggesterStation[] => {
    const kept = stations.filter((s) => !claimed.has(s.id))
    for (const s of kept) claimed.add(s.id)
    return kept
  }

  // Claim in PRECEDENCE order (highest first), independent of display order.
  const favoritesShown = claim(favorites) // protected — nothing claimed yet
  const nearbyShown = claim(nearby)
  const recentsShown = claim(recents)

  // Budget fill (spec §3): the unique ids across the top three sections
  // count once each toward the 8-row budget (they are already unique post-
  // dedup); Popular gets the remainder, with stations already shown above
  // filtered out.
  const popularBudget = Math.max(0, IDLE_VISIBLE_BUDGET - claimed.size)
  const displayedPopular =
    popularBudget > 0
      ? popular.filter((station) => !claimed.has(station.id)).slice(0, popularBudget)
      : []

  // Emit in fixed DISPLAY order — Favorites → Nearby → Recents → Popular.
  const sections: SuggesterSection[] = []
  if (favoritesShown.length > 0) sections.push({ key: 'favorites', stations: favoritesShown })
  if (nearbyShown.length > 0) sections.push({ key: 'nearby', stations: nearbyShown })
  if (recentsShown.length > 0) sections.push({ key: 'recents', stations: recentsShown })
  if (displayedPopular.length > 0) sections.push({ key: 'popular', stations: displayedPopular })
  return sections
}
